import { useEffect, useState } from 'react';
import type { Device, DeviceWifiAp, DeviceWifiEvent, DeviceWifiPin, DeviceWifiPinMode } from '@cornerstone/shared';
import { formatNetworkStatus, wifiPinBlockReason } from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { useScopedUrl } from '../nav/scopeOrg';
import { Button } from './ui';
import { InfoTile } from './DeviceRuntime';
import { describeApiError, toast } from './toast';
import { cn } from '../utils/cn';

// =============================================================================
// 원격 Wi-Fi 진단 (APK 0.5.11+)
//
// · 상태 즉시 확인 — reportStatus 명령. 서버가 WebSocket 으로 기기를 깨워 30초 주기를
//   기다리지 않고 새 상태를 보고하게 한다. 끝나면 상세 화면을 다시 읽는다.
// · 주변 AP — 기기가 마지막 스캔에서 잡은 AP 전부(최대 20개). 고정은 지금 연결된 SSID 의 AP 만 가능.
// · AP 고정 — 같은 SSID 의 2.4/5GHz 를 오가며 끊기는 기기를 한 AP 에 묶는다. 기기가 60초 안에
//   인터넷을 확인하지 못하면 스스로 원복하므로 현장 없이 시도해도 기기를 잃지 않는다.
//   BRICK Lit 앱이 저장한 Wi-Fi 의 AP 주소만 그 자리에서 바꾼다(0.5.14+). 설정 앱이 저장한 Wi-Fi 는 고정하지 않는다.
//   0.5.12 는 설정을 지우고 다시 저장해 기기가 오프라인으로 남은 사고가 있었다(2026-10-06). 고정한 AP 에 5분 이상
//   연결되지 않으면 기기가 스스로 고정을 푼다.
// · 진행 추적 — 고정·해제는 연결을 잠시 끊는다. 명령 응답과 별개로 기기가 보고하는 wifiPin 이 바뀔 때까지(최대 120초) 따라간다.
// =============================================================================

type CommandState = { status: 'pending' | 'received' | 'success' | 'failed' | 'expired'; result: Record<string, unknown> | null; error: string | null };

const WAIT_MS = 20_000;
const PIN_MODE: Record<DeviceWifiPinMode, string> = {
  own: '원격 고정 가능 · BRICK Lit 앱에서 저장한 Wi-Fi · 해제 확인됨',
  unsupported: '원격 고정 불가',
  root: 'APK 0.5.15 이상 업데이트 필요',
  open: 'APK 0.5.15 이상 업데이트 필요',
  stored: 'APK 0.5.15 이상 업데이트 필요',
  passphrase: 'APK 0.5.15 이상 업데이트 필요',
};
/** 고정·해제 후 기기의 최종 보고를 기다리는 시간과 간격. 기기는 60초 확인 + 30초 재연결 확인까지 쓴다. */
const TRACK_MS = 120_000;
const TRACK_STEP_MS = 3_000;
/** 이보다 강하면 지금 AP 로도 충분하다 — 2.4GHz 로 묶으면 링크 속도만 떨어진다. */
const GOOD_RSSI = -60;
const PIN_STATE: Record<DeviceWifiPin['state'], string> = {
  applying: '연결 확인 중', applied: '고정됨', rolled_back: '자동 원복됨', failed: '실패', cleared: '고정 해제',
};

/** 명령을 보내고 기기가 끝낼 때까지(최대 20초) 기다린다. 시간 안에 끝나지 않으면 null. */
function useDeviceCommand(deviceId: string) {
  const scoped = useScopedUrl();
  return async (command: string, payload?: Record<string, unknown>): Promise<CommandState | null> => {
    const sent = await fetch(scoped(`${API_BASE}/api/devices/${encodeURIComponent(deviceId)}/commands`), {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload ? { command, payload } : { command }),
    });
    if (!sent.ok) throw new Error(`HTTP ${sent.status}`);
    const { id } = await sent.json() as { id: string };
    const until = Date.now() + WAIT_MS;
    while (Date.now() < until) {
      await new Promise(resolve => setTimeout(resolve, 1_000));
      const read = await fetch(scoped(`${API_BASE}/api/devices/${encodeURIComponent(deviceId)}/commands/${encodeURIComponent(id)}`), { cache: 'no-store' });
      if (!read.ok) continue;
      const state = await read.json() as CommandState;
      if (state.status === 'success' || state.status === 'failed' || state.status === 'expired') return state;
    }
    return null;
  };
}

const refreshDetail = () => window.dispatchEvent(new Event('bricklit:device-changed'));

export function DeviceStatusRefreshButton({ device, disabled }: { device: Device; disabled?: boolean }): JSX.Element {
  const run = useDeviceCommand(device.id);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const check = async () => {
    setBusy(true); setNote(null);
    try {
      const state = await run('reportStatus');
      if (!state) setNote('응답 없음 · 기기가 오프라인이거나 연결이 불안정합니다');
      else if (state.status === 'success') setNote(`방금 보고됨 · ${new Date().toLocaleTimeString('ko-KR')}`);
      else setNote(state.error ?? '기기가 상태를 보고하지 못했습니다');
      refreshDetail();
    } catch (error) {
      toast(describeApiError(error), 'error');
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex items-center gap-2">
    {note && <span role="status" className="text-[12px] text-text-dim">{note}</span>}
    <Button className="!px-2.5 !py-1 !text-[13px]" onClick={() => void check()} disabled={busy || disabled}>{busy ? '확인 중…' : '상태 즉시 확인'}</Button>
  </div>;
}

type Tracking = { kind: 'pin' | 'unpin'; previousAt: string | null; startedAt: number };
type Outcome = { tone: 'ok' | 'warn'; text: string };

const band = (mhz: number) => mhz >= 5900 ? '6GHz' : mhz >= 4900 ? '5GHz' : '2.4GHz';
const clock = (iso: string) => new Date(iso).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });

function outcomeOf(pin: DeviceWifiPin, aps: DeviceWifiAp[] | undefined): Outcome {
  const ap = (aps ?? []).find(item => item.bssid === pin.bssid);
  const where = `${pin.bssid}${ap ? ` (${band(ap.frequencyMhz)})` : ''}`;
  if (pin.state === 'applied') return { tone: 'ok', text: `고정됨 · ${where} 연결 확인` };
  if (pin.state === 'cleared') return { tone: 'ok', text: '고정 해제 · 가장 좋은 AP로 자동 연결' };
  if (pin.state === 'rolled_back') return { tone: 'warn', text: `자동 원복됨 · ${pin.message ?? ''}` };
  return { tone: 'warn', text: `실패 · ${pin.message ?? '기기가 변경하지 못했습니다'}` };
}

const EVENT_LABEL: Record<DeviceWifiEvent['type'], string> = { drop: '끊김', join: '재연결', band: '대역 전환' };
const eventLine = (event: DeviceWifiEvent) => `${new Date(event.at).toLocaleString('ko-KR')} ${EVENT_LABEL[event.type]}`
  + `${typeof event.frequencyMhz === 'number' ? ` · ${band(event.frequencyMhz)}` : ''}${typeof event.rssi === 'number' ? ` · ${event.rssi} dBm` : ''}`;

/** 현장 IT 담당자에게 그대로 보낼 수 있는 진단 리포트. 기기 이름·SSID·AP·이력·권장 조치만 담는다. */
export function wifiDiagnosticReport(device: Device): string {
  const native = device.nativeStatus ?? {};
  const own = (native.wifiAps ?? []).filter(ap => ap.ssid && ap.ssid === native.wifiSsid);
  return [
    `[BRICK Lit Wi-Fi 진단] ${device.name}`,
    `작성 ${new Date().toLocaleString('ko-KR')}`,
    `현재 연결: ${formatNetworkStatus(native) ?? '-'}${native.wifiBssid ? ` · AP ${native.wifiBssid}` : ''}`,
    `최근 1시간: 끊김 ${native.wifiDisconnects1h ?? '-'}회 · 대역 전환 ${native.wifiBandSwitches1h ?? '-'}회`,
    `같은 이름(${native.wifiSsid ?? '-'})의 AP:`,
    ...(own.length ? own.map(ap => `  - ${ap.bssid} · ${band(ap.frequencyMhz)} · ${ap.rssi} dBm`) : ['  - 보고 없음']),
    '최근 이벤트:',
    ...((native.wifiEvents ?? []).length ? (native.wifiEvents ?? []).map(event => `  - ${eventLine(event)}`) : ['  - 보고 없음']),
    '권장 조치:',
    '  1. 공유기에서 2.4GHz와 5GHz의 SSID를 분리하거나 band steering(대역 자동 전환)을 끄고, 기기를 신호가 좋은 대역에 연결',
    '  2. AP 위치·출력 조정으로 기기 위치의 신호를 -65 dBm 이상으로 확보',
    '  3. 가능하면 유선 LAN 연결',
  ].join('\n');
}

export function DeviceWifiCard({ device, canPin }: { device: Device; canPin: boolean }): JSX.Element | null {
  const native = device.nativeStatus;
  const run = useDeviceCommand(device.id);
  const scoped = useScopedUrl();
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState<Tracking | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  // 기기의 최종 보고(wifiPin 이 바뀌고 applying 이 아님)를 기다린다. 기기 시계와 브라우저 시계가 달라도 되게 이전 보고 시각과만 비교한다.
  useEffect(() => {
    if (!tracking) return;
    let stopped = false;
    const timer = window.setInterval(() => setElapsed(Math.round((Date.now() - tracking.startedAt) / 1000)), 1_000);
    void (async () => {
      while (!stopped) {
        if (Date.now() - tracking.startedAt > TRACK_MS) {
          setOutcome({ tone: 'warn', text: '확인 지연 · 기기가 다시 연결되면 결과가 자동으로 반영됩니다' });
          setTracking(null); refreshDetail(); return;
        }
        try {
          const response = await fetch(scoped(`${API_BASE}/api/devices/${encodeURIComponent(device.id)}`), { cache: 'no-store' });
          const latest = response.ok ? await response.json() as Device : null;
          const pin = latest?.nativeStatus?.wifiPin;
          if (!stopped && pin && pin.at !== tracking.previousAt && pin.state !== 'applying') {
            const result = outcomeOf(pin, latest?.nativeStatus?.wifiAps);
            setOutcome(result); setTracking(null); refreshDetail();
            toast(result.text, result.tone === 'ok' ? 'success' : 'error');
            return;
          }
        } catch { /* 기기가 바꾸는 동안의 일시 오류는 다음 확인에서 다시 본다 */ }
        await new Promise(resolve => setTimeout(resolve, TRACK_STEP_MS));
      }
    })();
    return () => { stopped = true; window.clearInterval(timer); };
  }, [tracking, device.id, scoped]);

  if (!native?.networkType) return null;

  const send = async (command: 'pinWifi' | 'unpinWifi', payload?: Record<string, unknown>) => {
    setBusy(true); setOutcome(null);
    try {
      const state = await run(command, payload);
      // 응답이 없어도 기기는 연결을 바꾸는 중일 수 있다 — 실패로 단정하지 않고 최종 보고를 기다린다.
      if (state && state.status !== 'success') { toast(state.error ?? '기기가 명령을 거부했습니다', 'error'); return; }
      setElapsed(0);
      setTracking({ kind: command === 'pinWifi' ? 'pin' : 'unpin', previousAt: native.wifiPin?.at ?? null, startedAt: Date.now() });
    } catch (error) {
      toast(describeApiError(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const pin = native.wifiPin;
  const pinned = pin && (pin.state === 'applied' || pin.state === 'applying') ? pin.bssid : null;
  const blocked = wifiPinBlockReason(device);
  const locked = busy || !!tracking;
  const since = native.wifiStatsSince && Date.now() - Date.parse(native.wifiStatsSince) < 3_600_000 ? ` (${clock(native.wifiStatsSince)}부터)` : '';
  const stability = typeof native.wifiDisconnects1h === 'number'
    ? `끊김 ${native.wifiDisconnects1h}회 · 대역 전환 ${native.wifiBandSwitches1h ?? 0}회${since}` : '보고 대기 · APK 0.5.11 이상';
  const strong = typeof native.wifiRssi === 'number' && native.wifiRssi >= GOOD_RSSI;

  return <InfoTile title="Wi-Fi 진단" icon="globe" className="md:col-span-2" rows={[
    { label: '현재 연결', value: formatNetworkStatus(native) ?? '-' },
    { label: '최근 1시간', value: stability },
    ...(native.wifiPinMode ? [{ label: '고정 방식', value: `${PIN_MODE[native.wifiPinMode]}${native.wifiPinMode === 'unsupported' && native.wifiPinProbe ? ` · ${native.wifiPinProbe}` : ''}` }] : []),
    ...(pin ? [{ label: 'AP 고정', value: `${PIN_STATE[pin.state]} · ${pin.bssid}${pin.message ? ` · ${pin.message}` : ''}` }] : []),
  ]}>
    {tracking && <div role="status" className="mt-3 rounded border border-accent/40 bg-accent/10 px-3 py-2 text-[13px]">
      {tracking.kind === 'pin' ? 'AP 변경 중' : '고정 해제 중'} · 연결이 잠시 끊길 수 있습니다 · {elapsed}초
    </div>}
    {!tracking && outcome && <div role="status" className={cn('mt-3 rounded border px-3 py-2 text-[13px]', outcome.tone === 'ok' ? 'border-green/40 bg-green/10' : 'border-red/40 bg-red/10')}>{outcome.text}</div>}
    {native.wifiAps && native.wifiAps.length > 0 && <div className="mt-3 max-h-80 overflow-auto">
      <table className="w-full text-[13px]" aria-label="주변 AP">
        <caption className="mb-1 text-left text-[12px] text-text-dim">기기가 마지막 스캔에서 잡은 AP · 신호 강한 순 · 최대 20개</caption>
        <thead><tr className="text-left text-text-dim"><th className="font-normal">SSID</th><th className="font-normal">AP 주소</th><th className="font-normal">대역</th><th className="font-normal">신호</th><th /></tr></thead>
        <tbody>{native.wifiAps.map(ap => {
          const own = !!native.wifiSsid && ap.ssid === native.wifiSsid;
          return <tr key={ap.bssid} className={cn('border-t border-line', !own && 'text-text-dim')}>
            <td className="py-1.5">{ap.ssid || '(숨김)'}</td>
            <td className="font-mono text-[12px]">{ap.bssid}{ap.bssid === native.wifiBssid && <span className="ml-1 text-accent">· 연결 중</span>}</td>
            <td>{band(ap.frequencyMhz)}</td>
            <td>{ap.rssi} dBm</td>
            <td className="text-right">{canPin && own && (pinned === ap.bssid
              ? <Button className="!px-2.5 !py-1 !text-[13px]" disabled={locked} onClick={() => void send('unpinWifi')}>고정 해제</Button>
              : !blocked && <Button className="!px-2.5 !py-1 !text-[13px]" disabled={locked} onClick={() => void send('pinWifi', { bssid: ap.bssid })}>이 AP로 고정</Button>)}</td>
          </tr>;
        })}</tbody>
      </table>
    </div>}
    {native.wifiEvents && native.wifiEvents.length > 0 && <details className="mt-3 text-[13px]">
      <summary className="cursor-pointer text-text-dim">최근 Wi-Fi 이벤트 {native.wifiEvents.length}건</summary>
      <ol aria-label="최근 Wi-Fi 이벤트" className="mt-1 max-h-48 overflow-auto font-mono text-[12px]">
        {[...native.wifiEvents].reverse().map(event => <li key={`${event.at}-${event.type}`}>{eventLine(event)}</li>)}
      </ol>
    </details>}
    <div className="mt-3">
      <Button className="!px-2.5 !py-1 !text-[13px]" onClick={() => {
        void navigator.clipboard.writeText(wifiDiagnosticReport(device))
          .then(() => toast('진단 리포트를 복사했습니다 · 현장 IT 담당자에게 전달하세요', 'success'))
          .catch(() => toast('복사하지 못했습니다 · 브라우저의 클립보드 권한을 확인하세요', 'error'));
      }}>진단 리포트 복사</Button>
    </div>
    {canPin && blocked && <p role="note" className="mt-2 text-[12px] text-text-dim">{blocked}</p>}
    {canPin && !blocked && strong && <p className="mt-2 text-[12px] text-text-dim">현재 신호 양호({native.wifiRssi} dBm) · 끊김이 없다면 고정하지 않아도 됩니다.</p>}
    {canPin && !blocked && <p className="mt-2 text-[12px] text-text-dim">AP를 바꾸는 동안 연결이 잠시 끊깁니다. 60초 안에 인터넷 연결이 확인되지 않거나, 나중에 고정한 AP에 5분 이상 연결되지 않으면 기기가 스스로 고정을 풉니다.</p>}
  </InfoTile>;
}
